import {
  Box,
  Button,
  InputBase,
  TableCell,
  TableRow,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import CustomTable from "../../components/common-components/CustomTable";
import {
  AddIcon,
  AdminEditIconTable,
  DeleteIconTable,
} from "../../assets/Icon/ArrowIcon";
import SearchIcon from "@mui/icons-material/Search";
import AccountStatusButton from "../../components/common-components/AccountStatusButton";
import { useAppDispatch } from "../../stores/store";
import { useSelector } from "react-redux";
import CommonPagination from "../../components/common-components/CommonPagination";
import DeleteModal from "./DeleteService";
import ListLoadingNew from "../../components/common-components/ListLoadingNew";
import { useNavigate } from "react-router-dom";
import NoDataFound from "../../components/common-components/NoDataFound";
import { CrossIcon } from "../../assets/Icon/ModalIcon";
import {
  CloseButton,
  CloseButtonWrapper,
} from "../../components/style/modalStyleLarge";
import { getServiceList } from "../../stores/services";

const Services = () => {
  const dispatch = useAppDispatch();
  const {
    data: serviceList,
    total_records,
    serviceListStatus,
  } = useSelector((state: any) => state.services);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<any>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    fetchServices();
  }, [page, limit, debouncedSearch]);

  const fetchServices = async () => {
    try {
      console.log("0. Starting fetchServices, current loading:", loading);
      setLoading(true);

      const offset = (page - 1) * limit;
      console.log("1. Fetching services with params:", {
        offset,
        limit,
        search: searchQuery.trim(),
      });

      const response = await dispatch<any>(
        getServiceList({
          offset,
          limit,
          search: searchQuery.trim(),
        })
      );

      console.log("2. API Response received:", response);
      console.log("3. Current Redux State:", {
        serviceList,
        total_records,
        serviceListStatus,
        loading,
        responsePayload: response.payload,
      });
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  // Add useEffect to monitor loading state
  useEffect(() => {
    console.log("Loading state changed:", loading);
  }, [loading]);

  // Add useEffect to monitor serviceList changes
  useEffect(() => {
    console.log("ServiceList changed:", serviceList);
  }, [serviceList]);

  // Add detailed logging for state changes
  useEffect(() => {
    console.log("Services State Changed:", {
      serviceList,
      total_records,
      serviceListStatus,
      loading,
      isArray: Array.isArray(serviceList),
      length: serviceList?.length,
      firstItem: serviceList?.[0],
    });
  }, [serviceList, total_records, serviceListStatus, loading]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setCurrentPage(newPage);
  };
  const handleOpen = () => setOpenModal(true);
  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedId(null);
    setPage(1);
    setCurrentPage(1);
  };

  const handleOpenDeleteModal = (agentId: number) => {
    setSelectedId(agentId);
    setOpenDeleteModal(true);
  };

  const formatToTimeZone = (
    dateInput: string | Date,
    timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: string = "en-US",
    options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }
  ): string => {
    if (!dateInput) return "Invalid Date";

    const date = new Date(
      typeof dateInput === "string" && !dateInput.endsWith("Z")
        ? dateInput + "Z"
        : dateInput
    );

    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    return new Intl.DateTimeFormat(locale, {
      timeZone,
      ...options,
    }).format(date);
  };

  return (
    <StyledBox sx={{ paddingY: "16px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          padding: "20px",
          "@media (min-width: 600px)": {
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          },
        }}
      >
        <Title>Services</Title>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            "@media (min-width: 600px)": {
              flexDirection: "row",
              alignItems: "center",
            },
          }}
        >
          <SearchWrapper>
            <SearchIcon />
            <StyledInputBase
              placeholder="Search here..."
              inputProps={{ "aria-label": "search" }}
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <CloseButtonWrapper>
                <CloseButton
                  onClick={() => {
                    setSearchQuery("");
                  }}
                  sx={{
                    cursor: "pointer",
                    display: "inline-flex",
                    color: "red",
                    position: "absolute",
                    right: "1px",
                    top: "6.5px",
                  }}
                >
                  <CrossIcon />
                </CloseButton>
              </CloseButtonWrapper>
            )}
          </SearchWrapper>
          <StyledButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/admin-services/create`)}
          >
            Add Service
          </StyledButton>
        </Box>
      </Box>
      <CustomTable
        headings={[
          "Sr. No",
          "Service Name",
          "Price",
          "Created",
          "Update",
          "Action",
        ]}
      >
        <>
          {loading && (
            <TableRow>
              <TableCell
                colSpan={7}
                sx={{
                  padding: "16px 24px",
                }}
              >
                <ListLoadingNew />
              </TableCell>
            </TableRow>
          )}

          {!loading && (!serviceList || serviceList.length === 0) && (
            <TableRow>
              <TableCell colSpan={7}>
                <NoDataFound
                  title="No Services Found"
                  subTitle="Try adding a new service or modifying your search criteria to find what you're looking for."
                   status={true}
                />
              </TableCell>
            </TableRow>
          )}

          {!loading && serviceList && serviceList.length > 0 && (
            <>
              {console.log("Rendering service list:", serviceList)}
              {serviceList.map((item: any, index: number) => {
                console.log("Rendering item:", item);
                return (
                  <TableRow key={item.id || index}>
                    <TableCell
                      sx={{
                        padding: "16px 24px",
                      }}
                    >
                      <Typography>{(page - 1) * limit + index + 1}</Typography>
                    </TableCell>
                    {/* <TableCell
                      sx={{
                        padding: "16px 24px",
                      }}
                    >
                      <Typography>{item?.service_name || "-"}</Typography>
                    </TableCell> */}
                    <TableCell
                      sx={{
                        padding: "16px 24px",
                        maxWidth: 200,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <Tooltip title={item.service_name || ""}>
                        <Typography component="span">
                          {item.service_name && item.service_name.length > 40
                            ? `${item.service_name.slice(0, 40)}…`
                            : item.service_name || "-"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px 24px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography>
                        {" "}
                        {item.price != null ? `$${item.price}` : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px 24px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography>
                        {item?.created_at
                          ? formatToTimeZone(item.created_at)
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px 24px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography>
                        {item?.updated_at
                          ? formatToTimeZone(item.updated_at)
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="start"
                        paddingLeft="10px"
                        gap={1}
                      >
                        {item?.id && (
                          <>
                            <Box
                              onClick={() => handleOpenDeleteModal(item.id)}
                              style={{ cursor: "pointer" }}
                            >
                              <DeleteIconTable />
                            </Box>
                            <Box
                              onClick={() =>
                                navigate(`/admin-services/${item?.id}`)
                              }
                              style={{ cursor: "pointer" }}
                            >
                              <AdminEditIconTable />
                            </Box>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </>
          )}
        </>
      </CustomTable>

      {/* Pagination */}
      {Math.ceil(total_records / limit) > 1 && (
        <Box sx={{ display: "flex", justifyContent: "end", mt: 3 }}>
          <CommonPagination
            currentPage={page}
            totalPages={Math.ceil(total_records / limit)}
            onPageChange={handlePageChange}
          />
        </Box>
      )}

      <DeleteModal
        open={openDeleteModal}
        handleClose={handleCloseDeleteModal}
        id={selectedId}
        // onSuccess={()=>{
        //   // setPage(1);
        //   // setCurrentPage(1);
        //   setTimeout(()=>{
        //     fetchServices()
        //   },100)

        // }}
      />
    </StyledBox>
  );
};

export default Services;

const StyledBox = styled(Box)({
  background: "#ffffff",
  boxShadow: "0px 2px 40px 0px #DDE3EA33",
  borderRadius: "10px",
});

const Container = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "20px",
});

const Title = styled(Typography)({
  color: "#01060B !important",
  fontSize: "20px !important",
  fontWeight: "600 !important",
});

const SearchContainer = styled(Box)({
  display: "flex",
  gap: "10px",
  alignItems: "center",
});

const SearchWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  background: "#F6F9FD",
  color: "#6B6B6B",
  borderRadius: "8px",
  padding: "5px 10px",
  position: "relative",
  "&:hover": {
    background: "#F6F9FD",
  },
});

const StyledButton = styled(Button)({
  width: "120px !important",
  whiteSpace: "nowrap",
  borderRadius: "6px !important",
  height: "40px !important",
});

const StyledInputBase = styled(InputBase)({
  minWidth: "300px",
  padding: "3px",
});
